import React from 'react';
import UpdateLayout, { ExternalLink } from './UpdateLayout';
import SnowflakeTimeline from './SnowflakeTimeline';
import { SNOWFLAKE_2026_SOURCES, DELIVERY_CONTEXT } from '../../../content/snowflakeUpdates';

export default function SnowflakeSoFarPost() {
  return (
    <UpdateLayout
      path="/updates/snowflake-2026-so-far"
      title="Snowflake in 2026 so far: agents, semantic views, open formats"
      sources={SNOWFLAKE_2026_SOURCES}
    >
      <p>
        Snowflake spent this year making the semantic view the governed object that agents and BI
        share. Three changes matter if that view is how your team defines meaning.
      </p>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        Cortex Agents are the main way in
      </h2>
      <p>
        On April 13, agents that use a semantic view started generating the SQL themselves (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/other/2026-04-13-cortex-agents-agentic-analyst">
          release note
        </ExternalLink>
        ). On August 28, Snowflake recommended moving from Cortex Analyst to Cortex Agents. The
        Analyst API remains available (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/other/2026-08-28-cortex-analyst-transition-cortex-agents">
          release note
        </ExternalLink>
        ). At Summit on June 2, Snowflake Intelligence was renamed Snowflake CoWork, and Cortex Code
        was renamed Snowflake CoCo (
        <ExternalLink href="https://www.snowflake.com/en/news/press-releases/snowflake-cowork-powers-the-agentic-enterprise-as-the-personal-agent-for-knowledge-workers-to-work-smarter/">
          announcement
        </ExternalLink>
        ).
      </p>
      <p>
        The agent surface kept filling in around that recommendation. Evaluations went GA on March
        13. Guardrails for CoWork and Cortex Agents went GA on May 14. The code execution tool went
        GA on October 5, and the Cortex Agent SDK entered public preview on October 13.
      </p>
      <p>
        On October 13, Gemini 4 Argon entered public preview in Snowflake Cortex AI. It is available
        in Cortex AI Functions. Snowflake lists CoWork, CoCo, and Cortex Agents as coming soon for
        that model, on supported clouds, using cross-region inference (
        <ExternalLink href="https://www.snowflake.com/en/blog/gemini-4-argon-snowflake-cortex-ai/">
          Snowflake blog
        </ExternalLink>
        ).
      </p>
      <p>
        On October 14, document generation in Snowflake CoWork became generally available. CoWork can
        produce a PDF, a Word document (.docx), a PowerPoint presentation, or an HTML file from the
        analysis in the conversation. Generation requires the Cortex Agent code execution tool on
        the associated agent, and the file can be viewed inline (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/other/2026-10-14-cowork-document-generation-ga">
          release note
        </ExternalLink>
        ).
      </p>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        The semantic view is the governed core
      </h2>
      <p>
        Semantic View Autopilot went GA on February 3. It generates semantic views from query
        history and BI assets, with Tableau named first (
        <ExternalLink href="https://www.snowflake.com/en/news/press-releases/snowflake-delivers-semantic-view-autopilot-as-the-foundation-for-trusted-scalable-enterprise-ready-AI/">
          press release
        </ExternalLink>
        ).
      </p>
      <p>
        Power BI ingestion for Autopilot went GA on August 18. You upload a .pbit or .pbix file and
        Autopilot creates a semantic view from that model, including the measures, table
        relationships, and column descriptions already in the file. Report-level measures and
        time-intelligence functions are not fully supported (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/other/2026-08-18-semantic-views-power-bi-ingestion-ga">
          release note
        </ExternalLink>
        ).
      </p>
      <p>
        Semantic Studio, the authoring environment in Snowsight Workspaces, entered public preview
        on August 26 and went GA on September 30. You edit the view there, including from Git.
        Committing the file and deploying it onto the live semantic view are separate actions (
        <ExternalLink href="https://docs.snowflake.com/en/user-guide/views-semantic/semantic-studio">
          Semantic Studio
        </ExternalLink>
        ,{' '}
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/other/2026-09-30-semantic-studio-ga">
          GA note
        </ExternalLink>
        ). The same day, DCM Projects gained DEFINE SEMANTIC VIEW, so that definition can be planned
        and deployed as code (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/other/2026-09-30-dcm-define-semantic-view-ga">
          release note
        </ExternalLink>
        ).
      </p>

      <h2 className="pt-2 font-display text-2xl font-semibold tracking-tight text-brand-900">
        Open formats, including two-way Fabric access
      </h2>
      <p>
        Apache Iceberg v3 went GA on May 7 (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/other/2026-05-07-iceberg-v3-ga">
          release note
        </ExternalLink>
        ). Two-way Iceberg access with Microsoft Fabric went GA on January 30: Fabric can query
        Snowflake-managed Iceberg tables, and Snowflake can query OneLake Iceberg tables through a
        REST catalog integration (
        <ExternalLink href="https://docs.snowflake.com/en/release-notes/2026/other/2026-01-30-iceberg-microsoft-fabric-bidirectional-data-access-ga">
          release note
        </ExternalLink>
        ). External engines can also write Snowflake-managed Iceberg tables, generally available May
        26, and Snowflake storage for Iceberg, with no external volume to set up, went GA on June 1.
      </p>

      <div className="pt-4">
        <SnowflakeTimeline />
      </div>

      <h2 className="pt-4 font-display text-2xl font-semibold tracking-tight text-brand-900">
        What this means for your team
      </h2>
      <p>
        Keep one semantic view as the definition agents and BI share. Semantic Studio, generally
        available September 30, is where that definition is authored. Git versions the file. Deploy
        is what publishes it. {DELIVERY_CONTEXT}
      </p>
      <p>
        Cortex Agents are the path Snowflake now recommends for questions against that view. A new
        model in Cortex AI Functions, or a CoWork file at the end of a conversation, sits on top of
        the view. It does not replace the view.
      </p>
      <p>
        Two checks belong with the view itself. Queries that ask for FACTS and DIMENSIONS together
        are the BCR-2287 change in the 2026_07 bundle, covered in{' '}
        <a href="/updates/behavior-change-bundles" className="font-medium text-brand-700 underline decoration-brand-400 underline-offset-2 hover:text-brand-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700">
          behavior change bundles
        </a>
        . And if the tables under the view are Iceberg, Fabric and other engines may already be able
        to read them. The meaning still lives in the semantic view. If that view needs to move to
        another tool,{' '}
        <a href="/updates/ossie-semantic-view-interchange" className="font-medium text-brand-700 underline decoration-brand-400 underline-offset-2 hover:text-brand-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700">
          Apache Ossie (Incubating)
        </a>{' '}
        import and export are in preview.
      </p>
    </UpdateLayout>
  );
}
